// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Render-agnostic markdown TOKENIZER for Khora answers. It parses a common markdown subset (headers,
// bold/italic, inline code, fenced code, ordered/unordered lists, tables, blockquotes, horizontal
// rules, links) into a neutral token tree. The two renderers — ChatMarkdown (→ React elements) and
// chatExport (→ a static HTML string) — walk the SAME tree, so the in-chat view and the exported
// document can't drift. It emits NO markup and does NO escaping: producing safe output (escaped text,
// an allow-listed link scheme) is entirely the renderer's job, and each renderer keeps its own policy
// (a link node carries the RAW href; the renderer decides whether it becomes an anchor).

// ---- inline tokens ---------------------------------------------------------------------------
export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'code'; value: string }
  | { type: 'bold'; children: InlineNode[] }
  | { type: 'italic'; children: InlineNode[] }
  // `value` = the display text (a leaf — NOT re-parsed, matching both original renderers); `href` = the
  // RAW target the renderer must scheme-check before turning it into a link.
  | { type: 'link'; value: string; href: string };

// Ordered by precedence; code first so nothing inside a `code` span is re-parsed.
const INLINE_RULES: { type: 'code' | 'bold' | 'link' | 'italic'; re: RegExp }[] = [
  { type: 'code', re: /`([^`]+)`/ },
  { type: 'bold', re: /\*\*([^*]+)\*\*|__([^_]+)__/ },
  { type: 'link', re: /\[([^\]]+)\]\(([^)\s]+)\)/ },
  { type: 'italic', re: /\*([^*]+)\*|_([^_]+)_/ },
];

// Parse one span of inline markdown: find the earliest-matching token, recurse on the text before/after
// it. bold/italic recurse into their inner text; a link's display text and a code span are leaves.
export const tokenizeInline = (text: string): InlineNode[] => {
  let earliest: { type: string; index: number; length: number; groups: RegExpMatchArray } | null = null;
  INLINE_RULES.forEach(({ type, re }) => {
    const match = text.match(re);
    if (match && match.index !== undefined && (earliest === null || match.index < earliest.index)) {
      earliest = { type, index: match.index, length: match[0].length, groups: match };
    }
  });
  if (earliest === null) return text ? [{ type: 'text', value: text }] : [];
  const hit = earliest as { type: string; index: number; length: number; groups: RegExpMatchArray };
  const before = text.slice(0, hit.index);
  const after = text.slice(hit.index + hit.length);
  const inner = hit.groups[1] ?? hit.groups[2] ?? '';
  const nodes: InlineNode[] = [];
  if (before) nodes.push({ type: 'text', value: before });
  if (hit.type === 'code') nodes.push({ type: 'code', value: inner });
  else if (hit.type === 'bold') nodes.push({ type: 'bold', children: tokenizeInline(inner) });
  else if (hit.type === 'italic') nodes.push({ type: 'italic', children: tokenizeInline(inner) });
  else if (hit.type === 'link') nodes.push({ type: 'link', value: hit.groups[1], href: hit.groups[2] });
  nodes.push(...tokenizeInline(after));
  return nodes;
};

// ---- block tokens ----------------------------------------------------------------------------
export type Block =
  | { type: 'code'; text: string }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'heading'; level: number; text: string }
  | { type: 'hr' }
  | { type: 'blockquote'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'paragraph'; text: string };

const isTableRow = (line: string): boolean => /^\s*\|.*\|\s*$/.test(line);
const isDivider = (line: string): boolean => /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');
const splitCells = (line: string): string[] => line.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim());

// Parse a whole markdown document into block tokens.
export const tokenizeMarkdown = (text: string): Block[] => {
  const lines = (text ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  // An if/else-if chain (no `continue`) walks the lines; each branch consumes what it matched and
  // advances `i`. Order matters: fenced code and tables are checked before the line-level blocks.
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*```/.test(line)) { // fenced code
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1; // closing fence
      blocks.push({ type: 'code', text: body.join('\n') });
    } else if (isTableRow(line) && i + 1 < lines.length && isDivider(lines[i + 1])) { // table
      const header = splitCells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) { rows.push(splitCells(lines[i])); i += 1; }
      blocks.push({ type: 'table', header, rows });
    } else if (/^\s*(#{1,6})\s+/.test(line)) { // header
      const match = line.match(/^\s*(#{1,6})\s+(.*)$/) as RegExpMatchArray;
      blocks.push({ type: 'heading', level: match[1].length, text: match[2] });
      i += 1;
    } else if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { // horizontal rule
      blocks.push({ type: 'hr' });
      i += 1;
    } else if (/^\s*>\s?/.test(line)) { // blockquote
      const quote: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i += 1; }
      blocks.push({ type: 'blockquote', text: quote.join(' ') });
    } else if (/^\s*\d+\.\s+/.test(line) || /^\s*[-*]\s+/.test(line)) { // list (ordered 1. / unordered - *)
      const ordered = /^\s*\d+\.\s+/.test(line);
      const itemRe = ordered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/;
      const items: string[] = [];
      while (i < lines.length && itemRe.test(lines[i])) { items.push(lines[i].replace(itemRe, '')); i += 1; }
      blocks.push({ type: 'list', ordered, items });
    } else if (line.trim() === '') { // blank
      i += 1;
    } else { // paragraph
      // Gather consecutive non-blank, non-special lines. Seed with the current line and advance i FIRST,
      // so a line that reached this fallback — e.g. a table ROW with no `---` divider after it — is
      // always consumed. Otherwise the paragraph guard (`!isTableRow`) skips it, i never advances, and
      // the outer `while (i < lines.length)` revisits the same index forever and FREEZES the tab.
      const para: string[] = [lines[i]];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ''
        && !/^\s*(#{1,6}\s|```|>|\d+\.\s|[-*]\s)/.test(lines[i]) && !isTableRow(lines[i])) {
        para.push(lines[i]); i += 1;
      }
      blocks.push({ type: 'paragraph', text: para.join('\n') });
    }
  }
  return blocks;
};
